import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const ExecutiveBoardSection = () => {
  const executiveBoard = [
    {
      id: 1,
      name: "Kavinda Perera",
      position: "President",
      profession: "Software Engineer at WSO2",
      education: "BSc Computer Science - University of Colombo",
      joinYear: 2021,
      email: "kavinda.perera@leocolombo.lk",
      linkedin: "linkedin.com/in/kavindaperera",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      achievements: [
        "Led 15+ community service projects",
        "Organized annual Leo Convention 2024",
        "Increased membership by 40% during tenure"
      ],
      focusAreas: ["Youth Development", "Technology Education", "Environmental Conservation"],
      quote: "Leadership is not about being in charge. It\'s about taking care of those in your charge."
    },
    {
      id: 2,
      name: "Sanduni Fernando",
      position: "Vice President",
      profession: "Marketing Manager at Dialog Axiata",
      education: "MBA - Postgraduate Institute of Management",
      joinYear: 2020,
      email: "sanduni.fernando@leocolombo.lk",
      linkedin: "linkedin.com/in/sandunifernando",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face",
      achievements: [
        "Launched successful fundraising campaigns",
        "Established corporate partnerships worth LKR 2M+",
        "Mentored 25+ new members"
      ],
      focusAreas: ["Community Outreach", "Corporate Relations", "Women Empowerment"],
      quote: "Service to others is the rent you pay for your room here on earth."
    },
    {
      id: 3,
      name: "Ravindu Silva",
      position: "Secretary",
      profession: "Legal Associate at Julius & Creasy",
      education: "LLB - University of Colombo",
      joinYear: 2022,
      email: "ravindu.silva@leocolombo.lk",
      linkedin: "linkedin.com/in/ravindusilva",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      achievements: [
        "Streamlined club documentation system",
        "Coordinated international Leo exchanges",
        "Established legal aid clinic for community"
      ],
      focusAreas: ["Legal Aid", "Documentation", "International Relations"],
      quote: "Excellence is never an accident. It is always the result of high intention and intelligent execution."
    },
    {
      id: 4,
      name: "Tharushi Wickramasinghe",
      position: "Treasurer",
      profession: "Financial Analyst at Commercial Bank",
      education: "BSc Accounting & Finance - SLIIT",
      joinYear: 2021,
      email: "tharushi.wickramasinghe@leocolombo.lk",
      linkedin: "linkedin.com/in/tharushiwickramasinghe",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
      achievements: [
        "Maintained 100% financial transparency",
        "Secured LKR 5M+ in project funding",
        "Implemented digital payment systems"
      ],
      focusAreas: ["Financial Management", "Fundraising", "Digital Innovation"],
      quote: "Financial stewardship is about maximizing impact, not just managing money."
    }
  ];

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Executive Board 2024-25
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Meet the dedicated leaders driving our mission forward. Our executive board combines 
            professional excellence with passionate service commitment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {executiveBoard.map((member) => (
            <div key={member.id} className="bg-card rounded-xl card-shadow hover:card-shadow-hover transition-smooth overflow-hidden">
              <div className="relative">
                <div className="aspect-square overflow-hidden">
                  <Image
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover hover:scale-105 transition-smooth"
                  />
                </div>
                <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                  {member.position}
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-2">{member.name}</h3>
                <p className="text-primary font-medium mb-1">{member.profession}</p>
                <p className="text-sm text-muted-foreground mb-4">{member.education}</p>

                <div className="mb-4">
                  <p className="text-sm font-medium text-foreground mb-2">Focus Areas:</p>
                  <div className="flex flex-wrap gap-1">
                    {member.focusAreas.map((area, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-foreground mb-2">Key Achievements:</p>
                  <ul className="space-y-1">
                    {member.achievements.slice(0, 2).map((achievement, index) => (
                      <li key={index} className="text-xs text-muted-foreground flex items-start">
                        <Icon name="CheckCircle" size={12} className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>

                <blockquote className="text-xs italic text-muted-foreground border-l-2 border-primary pl-3 mb-4">
                  "{member.quote}"
                </blockquote>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Since {member.joinYear}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <a
                      href={`mailto:${member.email}`}
                      className="p-1 text-muted-foreground hover:text-primary transition-smooth"
                    >
                      <Icon name="Mail" size={14} />
                    </a>
                    <a
                      href={`https://${member.linkedin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-muted-foreground hover:text-primary transition-smooth"
                    >
                      <Icon name="Linkedin" size={14} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExecutiveBoardSection;